import React, { useState, useRef, Children } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { CTAButtonLight } from '../ui/CTAButton';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.12,
      ease: 'easeOut'
    }
  })
};
const faqs = [{
  q: "We've tried recruiting agencies before and they didn't deliver.",
  a: "Most agencies dump a pile of applicants on your lap and walk away. We don't. We run the entire process — from job listings to triple-screened final interviews booked right onto your calendar. You only show up to interview qualified, commission-ready reps. We also train you on our proprietary onboarding system so reps launch fast and drive real profit."
}, {
  q: "We're already spending on Indeed/ZipRecruiter.",
  a: "Indeed and ZipRecruiter are job boards — they give you volume, not quality. You still have to screen, filter, and chase. Our system handles all of that. The average cost-per-qualified-hire on job boards is $2,800+. Our clients average $850. The math isn't close."
}, {
  q: "What if we don't get results in 30 days?",
  a: "If you meet the ad budget minimum, and we don't generate strong early results, we keep working for free until we do. This isn't a gamble — it's a recruiting engine built to win fast or keep grinding until it does."
}, {
  q: "We don't have time to manage another tool.",
  a: "You don't manage anything. We plug into your current workflow. You don't need recruiters or extra staff. We build, manage, and optimize the system. You show up to interviews. That's it."
}];
function FAQAccordion({
  faq,
  index
}: {
  faq: (typeof faqs)[0];
  index: number;
}) {
  const [open, setOpen] = useState(false);
  return <motion.div custom={index + 1} variants={fadeUp} className="border-b border-[#1A2B4A]/10 last:border-0" data-id="element-339">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left gap-4" data-id="element-340">
        <span className="text-base sm:text-lg font-semibold text-[#1A2B4A]" data-id="element-341">
          {faq.q}
        </span>
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#4ECDC4]/15 flex items-center justify-center" data-id="element-342">
          {open ? <MinusIcon className="w-4 h-4 text-[#4ECDC4]" data-id="element-343" /> : <PlusIcon className="w-4 h-4 text-[#4ECDC4]" data-id="element-344" />}
        </div>
      </button>
      <AnimatePresence data-id="element-345">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="overflow-hidden" data-id="element-346">
            <p className="pb-5 text-[#1A2B4A]/65 leading-relaxed text-sm sm:text-base" data-id="element-347">
              {faq.a}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function ObjectionSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.1
  });
  return <section className="w-full bg-[#F5F7FA] py-24 px-6" data-id="element-348">
      <div ref={ref} className="max-w-3xl mx-auto" data-id="element-349">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-14" data-id="element-350">
          <span className="inline-block bg-[#4ECDC4] text-[#1A2B4A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-351">
            Common Questions
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#1A2B4A] mb-4 leading-tight" data-id="element-352">
            You've Probably Tried This Before.
          </h2>
          <p className="text-lg text-[#1A2B4A]/60" data-id="element-353">
            Here's why this is different.
          </p>
        </motion.div>

        <motion.div initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={{
        visible: {
          transition: {
            staggerChildren: 0.08
          }
        }
      }} className="bg-white rounded-2xl shadow-md px-6 sm:px-8 mb-12" data-id="element-354">
          {faqs.map((faq, i) => <FAQAccordion key={i} faq={faq} index={i} data-id="element-355" />)}
        </motion.div>

        <motion.div custom={6} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex justify-center" data-id="element-356">
          <CTAButtonLight data-id="element-357" />
        </motion.div>
      </div>
    </section>;
}